/**
 * Typed `fetch` wrappers around the relayer's chain-read endpoints.
 *
 * Every on-chain read the dApp used to do directly via toncenter is now
 * proxied through the relayer — so the dApp ships ZERO toncenter API
 * key (the relayer holds its own server-side key and stays under its
 * own rate limit).
 *
 * Helpers are intentionally small + side-effect-free; each maps 1:1 to
 * a relayer route. Use `getRelayerBase()` to compute the URL prefix —
 * for the chain-read endpoints we want the configured RELAYER_URL,
 * because the relayer's view of mainnet/testnet IS the right view.
 */
import { RELAYER_URL } from "./env";

function base(): string {
  return RELAYER_URL.replace(/\/+$/, "");
}

async function relayerGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(base() + path);
  for (const [k, v] of Object.entries(params ?? {})) url.searchParams.set(k, v);
  const res = await fetch(url.toString(), { headers: { accept: "application/json" } });
  if (!res.ok) {
    // The relayer's sendServerError / sendClientError helpers return a
    // body of shape `{ error: string }`. Surface that string so the
    // dApp's friendlyError mapper has something to pattern-match.
    let detail = "";
    try {
      detail = ((await res.json()) as { error?: string }).error ?? "";
    } catch {
      // body not JSON; leave detail empty
    }
    throw new Error(`relayer ${path} ${res.status}${detail ? `: ${detail}` : ""}`);
  }
  return (await res.json()) as T;
}

// --------- pool ---------

export async function poolIsKnownRoot(pool: string, root: bigint): Promise<boolean> {
  const resp = await relayerGet<{ isKnown: boolean }>("/pool/is-known-root", {
    pool,
    root: "0x" + root.toString(16),
  });
  return resp.isKnown;
}

/**
 * Authoritative on-chain spent check. Use this for the dApp's withdraw
 * pre-flight — the relayer's indexer-backed `/note/check` can be
 * seconds stale, and showing the user a misleading "ready to withdraw"
 * state is a worse UX than the small cost of a direct get-method call.
 */
export async function poolIsSpent(pool: string, nullifierHash: bigint): Promise<boolean> {
  const resp = await relayerGet<{ isSpent: boolean }>("/pool/is-spent", {
    pool,
    nullifier: "0x" + nullifierHash.toString(16),
  });
  return resp.isSpent;
}

// --------- governance ---------

export interface ProposalSnapshot {
  id: number;
  status: number;
  votingEnd: number;
  timelockEnd: number;
  /** bigint serialised as decimal string */
  forVotes: string;
  /** bigint serialised as decimal string */
  againstVotes: string;
  proposer: string;
  createdAt: number;
}

export async function getGovernanceProposal(
  governance: string,
  id: number
): Promise<ProposalSnapshot> {
  return relayerGet<ProposalSnapshot>("/governance/proposal", {
    governance,
    id: String(id),
  });
}

export interface ProposalPayload {
  dest: string;
  /** bigint serialised as decimal string */
  value: string;
  /** Cell encoded as base64 BOC. */
  bodyBase64: string;
}

export async function getGovernanceProposalPayload(
  governance: string,
  id: number
): Promise<ProposalPayload> {
  return relayerGet<ProposalPayload>("/governance/proposal-payload", {
    governance,
    id: String(id),
  });
}

// --------- staking ---------

export interface StakingLockBounds {
  minLockSeconds: number;
  maxLockSeconds: number;
}

export async function getStakingLockBounds(staking: string): Promise<StakingLockBounds> {
  return relayerGet<StakingLockBounds>("/staking/lock-bounds", { staking });
}

export type StakingAccount =
  | { isLocked: false }
  | {
      isLocked: true;
      /** Bigint as decimal string. */
      amount: string;
      lockEnd: number;
      /** Bigint as decimal string. */
      votingPower: string;
      /** Bigint as decimal string. */
      claimable: string;
    };

export async function getStakingAccount(staking: string, owner: string): Promise<StakingAccount> {
  return relayerGet<StakingAccount>("/staking/account", { staking, owner });
}

// --------- registry ---------

export interface ActiveRelayerRow {
  accountHash: string; // 0x... 256-bit
  address: string; // EQ... friendly
  status: "ACTIVE" | "UNBONDING" | "NOT_REGISTERED";
  bondedAt: number;
  unbondAt: number;
  metadataUri: string | null;
}

export async function listActiveRelayersViaRelayer(registry: string): Promise<ActiveRelayerRow[]> {
  const resp = await relayerGet<{ registry: string; relayers: ActiveRelayerRow[] }>(
    "/registry/active-relayers",
    { registry }
  );
  return resp.relayers;
}

// --------- jettons ---------

export async function getJettonWalletViaRelayer(master: string, owner: string): Promise<string> {
  const resp = await relayerGet<{ wallet: string }>("/jetton/wallet-address", {
    master,
    owner,
  });
  return resp.wallet;
}
